import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Users2, Target, FileCheck2, CircleDollarSign, Globe2, Layers, ShieldCheck, Trophy } from "lucide-react";

const StatCard = ({ label, value, hint, icon: Icon, accent, testid }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4" strokeWidth={2} />
      </div>
    </div>
    <div className="font-heading text-3xl font-bold text-slate-900 mt-4 tracking-tight">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    http.get("/dashboard/kpis").then((r) => setKpis(r.data));
  }, []);

  if (!kpis) return <Layout title="Dashboard"><div className="text-slate-500">Loading…</div></Layout>;

  const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <Layout title="Dashboard"
            subtitle={`Week of ${kpis.week_start} — ${kpis.week_end}`}>
      {/* Top row — objective this-week counts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Leads Generated" value={kpis.total_leads_generated}
                  hint={`${kpis.totals_all_time.leads} total in system`}
                  icon={Users2} accent="bg-slate-900 text-white" testid="kpi-leads-generated" />
        <StatCard label="Qualified Opportunities" value={kpis.total_qualified_opportunities}
                  hint="Passing V1–V3 + scored"
                  icon={Target} accent="bg-emerald-600 text-white" testid="kpi-qualified" />
        <StatCard label="Pipeline (weighted)" value={usd(kpis.estimated_weighted_pipeline)}
                  hint={`Gross: ${usd(kpis.estimated_pipeline_value)}`}
                  icon={CircleDollarSign} accent="bg-amber-600 text-white" testid="kpi-weighted-pipeline" />
        <StatCard label="Data Quality" value={`${kpis.data_quality_rate}%`}
                  hint="Leads passing V1–V3"
                  icon={ShieldCheck} accent="bg-slate-900 text-white" testid="kpi-data-quality" />
      </div>

      {/* Tier breakdown + coverage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Tier Breakdown</div>
              <div className="font-heading text-lg text-slate-900 font-semibold mt-1">This week's classified leads</div>
            </div>
            <Layers className="w-5 h-5 text-slate-400" />
          </div>
          <div className="space-y-4">
            {[
              { key: "Tier 1", cls: "bg-amber-500",  count: kpis.tier_breakdown["Tier 1"] },
              { key: "Tier 2", cls: "bg-slate-500",  count: kpis.tier_breakdown["Tier 2"] },
              { key: "Tier 3", cls: "bg-slate-300",  count: kpis.tier_breakdown["Tier 3"] },
            ].map((t) => {
              const total = Math.max(kpis.tier_breakdown["Tier 1"] + kpis.tier_breakdown["Tier 2"] + kpis.tier_breakdown["Tier 3"], 1);
              const pct = Math.round((t.count / total) * 100);
              return (
                <div key={t.key} data-testid={`tier-row-${t.key.replace(" ", "-").toLowerCase()}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${t.cls}`}></span>
                      <span className="font-medium text-slate-700">{t.key}</span>
                    </div>
                    <div className="font-mono text-slate-900 font-semibold">{t.count}</div>
                  </div>
                  <Progress value={pct} className="h-2" />
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-slate-900 rounded-lg p-6 text-white shadow-sm relative overflow-hidden">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Coverage</div>
          <div className="grid grid-cols-2 gap-6 mt-6">
            <div>
              <Globe2 className="w-5 h-5 text-amber-500 mb-2" />
              <div className="font-heading text-4xl font-bold tracking-tight">{kpis.countries_covered}</div>
              <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Countries</div>
            </div>
            <div>
              <Layers className="w-5 h-5 text-amber-500 mb-2" />
              <div className="font-heading text-4xl font-bold tracking-tight">{kpis.industries_covered}</div>
              <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Industries</div>
            </div>
          </div>
          <div className="mt-6 border-t border-slate-800 pt-4 text-xs text-slate-400">
            Scored leads all-time:{" "}
            <span className="text-white font-mono font-semibold">{kpis.totals_all_time.scored_leads}</span>
          </div>
        </div>
      </div>

      {/* Standup roll-ups */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="RFQs Identified"       value={kpis.total_rfqs_identified}       icon={FileCheck2} accent="bg-slate-100 text-slate-700" testid="kpi-rfqs" />
        <StatCard label="Quotations Submitted"  value={kpis.total_quotations_submitted}  icon={FileCheck2} accent="bg-slate-100 text-slate-700" testid="kpi-quotations" />
        <StatCard label="Orders Won"            value={kpis.total_orders_won}            icon={Trophy}     accent="bg-amber-100 text-amber-800" testid="kpi-orders-won" />
        <StatCard label="Revenue (this week)"   value={usd(kpis.revenue_generated_usd)}  icon={CircleDollarSign} accent="bg-emerald-100 text-emerald-700" testid="kpi-revenue" />
      </div>

      {/* Employee-of-week */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="employee-of-week">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Employee of the Week</div>
            <div className="font-heading text-lg text-slate-900 font-semibold mt-1">Ranked by business impact score</div>
          </div>
          <Trophy className="w-5 h-5 text-amber-600" />
        </div>
        {kpis.employee_of_week_candidates.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No activity yet this week.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {kpis.employee_of_week_candidates.map((e, i) => (
              <div key={e.employee} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600"}`}>{i + 1}</div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">{e.name}</div>
                    <div className="text-xs text-slate-500">{e.qualified} qualified · {e.companies} companies</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg font-semibold text-slate-900">{e.business_impact_score}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Impact</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
